import {
  decodeBase64Url,
  encodeBase64Url,
} from './primitives';

export interface SharedFolderGuestFragment {
  v: 1;
  fs: string;
  gw?: string;
  salt?: string;
  /** Optional guest UI root path (manifest-relative). Empty/omitted = whole folder. */
  rp?: string;
}

const FRAGMENT_SESSION_KEY = 'shareweb:folderFragment:';

export function buildSharedFolderGuestFragment(fragment: SharedFolderGuestFragment): string {
  const json = JSON.stringify(fragment);
  return encodeBase64Url(new TextEncoder().encode(json));
}

function decodeSharedFolderGuestFragment(hash: string): SharedFolderGuestFragment | null {
  try {
    const json = new TextDecoder().decode(decodeBase64Url(hash));
    const fragment = JSON.parse(json) as SharedFolderGuestFragment;
    if (fragment.v !== 1 || !fragment.fs) {
      return null;
    }

    if (!fragment.gw && !fragment.salt) {
      return null;
    }

    return fragment;
  } catch {
    return null;
  }
}

function cacheFragment(token: string, fragment: SharedFolderGuestFragment): void {
  sessionStorage.setItem(`${FRAGMENT_SESSION_KEY}${token}`, JSON.stringify(fragment));
}

function loadCachedFragment(token: string): SharedFolderGuestFragment | null {
  const cached = sessionStorage.getItem(`${FRAGMENT_SESSION_KEY}${token}`);
  if (!cached) {
    return null;
  }

  try {
    const fragment = JSON.parse(cached) as SharedFolderGuestFragment;
    if (fragment.v !== 1 || !fragment.fs) {
      return null;
    }

    if (!fragment.gw && !fragment.salt) {
      return null;
    }

    return fragment;
  } catch {
    return null;
  }
}

export function resolveSharedFolderGuestFragment(token: string): SharedFolderGuestFragment | null {
  const hash = window.location.hash.replace(/^#/, '');
  if (hash) {
    const fromHash = decodeSharedFolderGuestFragment(hash);
    if (fromHash) {
      cacheFragment(token, fromHash);
      history.replaceState(null, '', window.location.pathname + window.location.search);
      return fromHash;
    }
  }

  return loadCachedFragment(token);
}
